import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, inject, APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import * as i1$1 from 'keycloak-angular';
import { KeycloakAuthGuard, KeycloakEventType, KeycloakService, KeycloakAngularModule } from 'keycloak-angular';
import * as i1 from '@angular/router';
import { HttpContextToken, HTTP_INTERCEPTORS } from '@angular/common/http';
import { catchError, throwError, from, switchMap } from 'rxjs';

const keycloakConfig = {
    realm: 'myrealm',
    url: 'http://localhost:8080',
    clientId: 'angular-client',
};

class AuthGuard extends KeycloakAuthGuard {
    constructor(router, keycloakAngular, window) {
        super(router, keycloakAngular);
        this.router = router;
        this.keycloakAngular = keycloakAngular;
        this.window = window;
    }
    async isAccessAllowed(route, state) {
        // Force the user to log in if currently unauthenticated.
        if (!this.authenticated) {
            await this.keycloakAngular.login({
                redirectUri: this.window.location.origin + state.url,
            });
        }
        // Get the roles required from the route.
        const requiredRoles = route.data['roles'];
        // Allow the user to to proceed if no additional roles are required to access the route.
        if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
            return true;
        }
        // Allow the user to proceed if all the required roles are present.
        return requiredRoles.every((role) => this.roles.includes(role));
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthGuard, deps: [{ token: i1.Router }, { token: i1$1.KeycloakService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i1$1.KeycloakService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class AuthService {
    constructor(keycloakService, window) {
        this.keycloakService = keycloakService;
        this.window = window;
    }
    getLoggedUser() {
        try {
            const userDetails = this.keycloakService.getKeycloakInstance().idTokenParsed;
            return userDetails;
        }
        catch (e) {
            console.error('Exception', e);
            return undefined;
        }
    }
    isLoggedIn() {
        return this.keycloakService.isLoggedIn();
    }
    loadUserProfile() {
        return this.keycloakService.loadUserProfile();
    }
    login() {
        this.keycloakService.login();
    }
    logout() {
        this.keycloakService.logout(this.window.location.origin);
    }
    redirectToProfile() {
        this.keycloakService.getKeycloakInstance().accountManagement();
    }
    getRoles() {
        return this.keycloakService.getUserRoles();
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthService, deps: [{ token: i1$1.KeycloakService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.KeycloakService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

const WINDOW = new InjectionToken('An abstraction over global window object', {
    factory: () => {
        const { defaultView } = inject(DOCUMENT);
        if (!defaultView) {
            throw new Error('Window is not available');
        }
        return defaultView;
    },
});
function initializer(keycloak, KEYCLOAK_CONFIG, window) {
    const options = {
        config: KEYCLOAK_CONFIG || keycloakConfig,
        loadUserProfileAtStartUp: false,
        initOptions: {
            onLoad: 'check-sso',
            checkLoginIframe: true,
            silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        },
        bearerExcludedUrls: ['/assets', '/clients/public'],
        /*
        shouldAddToken: (request) => {
            const { method, url } = request;
            const isGetRequest = 'GET' === method.toUpperCase();
            const acceptablePaths = ['/assets', '/clients/public'];
            const isAcceptablePathMatch = acceptablePaths.some((path) => url.includes(path));
    
            return !(isGetRequest && isAcceptablePathMatch);
          },
        */
    };
    keycloak.keycloakEvents$.subscribe({
        next: (e) => {
            if (e.type == KeycloakEventType.OnTokenExpired) {
                console.log('Token expired and will be update');
                keycloak.updateToken(20);
            }
        },
    });
    return () => keycloak.init(options);
}
const KEYCLOAK_CONFIG = new InjectionToken('Keycloak');
class AuthModule {
    constructor(parentModule) {
        this.parentModule = parentModule;
    }
    static forRoot(config) {
        return {
            ngModule: AuthModule,
            providers: [
                AuthGuard,
                AuthService,
                { provide: KEYCLOAK_CONFIG, useValue: config },
                { provide: WINDOW, useValue: window },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializer,
                    multi: true,
                    deps: [KeycloakService, KEYCLOAK_CONFIG, WINDOW],
                },
            ],
        };
    }
    static forChild(config) {
        return {
            ngModule: AuthModule,
            providers: [{ provide: KEYCLOAK_CONFIG, useValue: config }, AuthGuard, AuthService],
        };
    }
}
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthModule, deps: [{ token: AuthModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.7", ngImport: i0, type: AuthModule, imports: [KeycloakAngularModule] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthModule, imports: [KeycloakAngularModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [KeycloakAngularModule],
                    providers: [],
                }]
        }], ctorParameters: function () { return [{ type: AuthModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

const IGNORE_REQUEST = new HttpContextToken(() => false);
/**
 * Usage example of IGNORE_REQUEST
 *
 * public login(creds: Credentials): Observable<any> {
 *    return this.httpClient
 *       .post<any>('/login', creds, {
 *          context: new HttpContext().set(IGNORE_REQUEST, true),
 *       })
 *       .pipe(
 *          tap((response) => {
 *             this.authService.setToken(response.token);
 *             this.router.navigate(['home']);
 *          })
 *       );
 * }
 */
class TokenInterceptor {
    constructor(kcService) {
        this.kcService = kcService;
    }
    intercept(request, next) {
        const authToken = this.kcService.getToken();
        const tokenize = { path: ['auth', 'api'], check: false };
        tokenize.path.forEach((path) => {
            if (request.url.includes(path)) {
                tokenize.check = true;
            }
        });
        if (request.context.get(IGNORE_REQUEST)) {
            console.log('IGNORE_REQUEST', request.urlWithParams);
            return next.handle(request).pipe(catchError((error) => throwError(() => error)));
        }
        return from(authToken).pipe(switchMap((token) => {
            if (tokenize.check) {
                return next.handle(request.clone({
                    setHeaders: {
                        Authorization: 'Bearer ' + token,
                    },
                }));
            }
            return next.handle(request).pipe(catchError((error) => throwError(() => error)));
        }));
    }
}
TokenInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: TokenInterceptor, deps: [{ token: i1$1.KeycloakService }], target: i0.ɵɵFactoryTarget.Injectable });
TokenInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: TokenInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: TokenInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.KeycloakService }]; } });
const TOKEN_INTERCEPTOR_PROVIDER = [{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }];

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthGuard, AuthModule, AuthService, IGNORE_REQUEST, KEYCLOAK_CONFIG, TOKEN_INTERCEPTOR_PROVIDER, TokenInterceptor, WINDOW, initializer };
